import { parseGoal, parseSchedule, toDayKey } from "./schedule";
import type { HabitGoal, HabitSchedule } from "./types";

/**
 * RESOLUTION of ACTIVE definition by date — core of "INTERPRETATION
 * SEALING".
 *
 * In habits "completed" status of a day is DERIVED (amount ≥ target) against
 * ACTIVE target of habit. Without versions, editing target 10→20 would re-evaluate
 * ALL past days against 20. For a past day to retain its verdict,
 * we reconstruct definition TIMELINE from `habit_version` rows
 * (server_ts = effective date) and resolve, for each date D,
 * "latest version with effectiveFrom (day-key) ≤ D".
 *
 * Backward compatibility: if habit has no versions (legacy) or only
 * one, resolution falls back to live definition ⇒ behavior
 * is IDENTICAL to past (existing streak/schedule tests remain unchanged).
 */

/** `habit_version` row as received from PowerSync bucket (SQLite). */
export interface HabitVersionRow {
  id?: string | null;
  habit_id: string | null;
  version_number: number | null;
  name: string | null;
  schedule_kind: string | null;
  weekdays: string | null;
  times_per_week: number | null;
  goal_kind: string | null;
  target_amount: number | null;
  target_unit: string | null;
  /** EFFECTIVE date (ISO timestamp). Converted to local day-key. */
  server_ts: string | null;
}

/** A definition with its effective date (local day-key). */
export interface TimelineEntry {
  effectiveFromDayKey: string;
  goal: HabitGoal;
  schedule: HabitSchedule;
}

export type HabitDefinitionTimeline = TimelineEntry[];

/** Resolved definition for a date (target + schedule). */
export interface ResolvedDefinition {
  goal: HabitGoal;
  schedule: HabitSchedule;
}

/**
 * Converts `server_ts` (ISO/UTC timestamp) to local day-key, the
 * same date convention used by completions (`toDayKey`).
 */
function serverTsToDayKey(serverTs: string | null): string | null {
  if (!serverTs) {
    return null;
  }
  const d = new Date(serverTs);
  if (Number.isNaN(d.getTime())) {
    return null;
  }
  return toDayKey(d);
}

/**
 * Builds timeline (ascending by effective date) for SINGLE habit
 * from its version rows. Rows without parseable `server_ts` are ignored.
 * Day-key ties: highest `version_number` wins (most recent rules
 * that day forward) — resolved by keeping it at end of stable sort.
 */
export function buildTimeline(rows: readonly HabitVersionRow[]): HabitDefinitionTimeline {
  const entries: (TimelineEntry & { versionNumber: number })[] = [];
  for (const row of rows) {
    const effectiveFromDayKey = serverTsToDayKey(row.server_ts);
    if (!effectiveFromDayKey) {
      continue;
    }
    entries.push({
      effectiveFromDayKey,
      versionNumber: row.version_number ?? 0,
      goal: parseGoal({
        goal_kind: row.goal_kind,
        target_amount: row.target_amount,
        target_unit: row.target_unit,
      }),
      schedule: parseSchedule({
        schedule_kind: row.schedule_kind,
        weekdays: row.weekdays,
        times_per_week: row.times_per_week,
      }),
    });
  }
  entries.sort((a, b) => {
    if (a.effectiveFromDayKey !== b.effectiveFromDayKey) {
      return a.effectiveFromDayKey < b.effectiveFromDayKey ? -1 : 1;
    }
    return a.versionNumber - b.versionNumber;
  });
  return entries.map(({ effectiveFromDayKey, goal, schedule }) => ({
    effectiveFromDayKey,
    goal,
    schedule,
  }));
}

/**
 * Active definition on `dateKey` = latest entry with `effectiveFrom ≤ dateKey`.
 * If none exists (empty timeline, or date earlier than first version) →
 * falls back to current LIVE definition (`currentGoal`/`currentSchedule`). Preserves
 * historical behavior for legacy habits without history.
 */
export function resolveDefinitionAt(
  timeline: HabitDefinitionTimeline,
  currentGoal: HabitGoal,
  currentSchedule: HabitSchedule,
  dateKey: string,
): ResolvedDefinition {
  let resolved: ResolvedDefinition | null = null;
  for (const entry of timeline) {
    if (entry.effectiveFromDayKey <= dateKey) {
      resolved = { goal: entry.goal, schedule: entry.schedule };
    } else {
      break; // ascending timeline: subsequent entries are also > dateKey
    }
  }
  return resolved ?? { goal: currentGoal, schedule: currentSchedule };
}

/**
 * Dates (day-keys) when definition CHANGED within [fromKey, toKey],
 * to render a marker on heatmap/stats ("definition changed on <date>").
 * Excludes first version (creation is not a "change" within range).
 */
export function definitionChangesInRange(
  timeline: HabitDefinitionTimeline,
  fromKey: string,
  toKey: string,
): string[] {
  const changes: string[] = [];
  for (let i = 1; i < timeline.length; i += 1) {
    const key = timeline[i]?.effectiveFromDayKey;
    if (key && key >= fromKey && key <= toKey) {
      changes.push(key);
    }
  }
  return changes;
}
